import { useAuth } from '@AuthProvider';
import { AB_EXPERIMENTS } from '@constants/ab-experiments';
import { ABTestStatus, ABUserInfo, ABVariantComponent } from '@models/abTest/experiment';
import { getExperimentVariant } from '@utils/ab-test';
import CryptoJS from 'crypto-js';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

export interface ABTestContext {
  experiments: ABUserInfo[];
  variantConditionalRender: (experimentId: string, components: ABVariantComponent[]) => React.ReactNode;
  isPartOfVariant: (experimentId: string, variantId: string) => boolean;
}

const ABTestContext = createContext<ABTestContext>({
  experiments: [],
  variantConditionalRender: () => null,
  isPartOfVariant: () => false,
});

export const ABTestContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, countryCode } = useAuth();
  const experiments = useMemo(() => {
    if (!user?.Uid) return [];
    return AB_EXPERIMENTS.map((experiment) => {
      // Check if experiment is active
      if (experiment.status !== ABTestStatus.ACTIVE) {
        return null;
      }
      // Check if user is in the correct country
      if (experiment.targetingConditions.country.length > 0) {
        if (countryCode && !experiment.targetingConditions.country.includes(countryCode)) {
          return null;
        }
      }
      let isIncludedInAnySegment = false;
      // Check if user is included in any of the segments
      if (experiment.targetingConditions.segments.length === 0) {
        isIncludedInAnySegment = true;
      } else {
        isIncludedInAnySegment = experiment.targetingConditions.segments.some((segment) =>
          Boolean(user?.[segment as keyof typeof user]),
        );
      }
      let hasAnyProp = false;
      // Check if user has any of the custom properties
      if (experiment.targetingConditions.customProperties.length === 0) {
        hasAnyProp = true;
      } else {
        hasAnyProp = experiment.targetingConditions.customProperties.some((prop) =>
          Boolean(user?.Account?.[prop.prop as keyof typeof user.Account] === prop.value),
        );
      }
      if (!isIncludedInAnySegment && !hasAnyProp) {
        return null;
      }
      const hashValue = CryptoJS.SHA1(user.Uid + experiment.id).toString();
      const selectedVariant = getExperimentVariant(hashValue, experiment.variants) ?? experiment.variants[0];
      experiment.enrollement(selectedVariant.name);
      return {
        experiment,
        selectedVariant,
      };
    }).filter((experiment) => experiment !== null);
  }, [user?.Uid, countryCode]);

  const isPartOfVariant = useCallback(
    (experimentId: string, variantId: string) => {
      const experimentInfo = experiments.find((exp) => exp.experiment.id === experimentId);
      return experimentInfo?.selectedVariant?.id === variantId;
    },
    [experiments],
  );

  const variantConditionalRender = useCallback(
    (experimentId: string, components: ABVariantComponent[]) => {
      const experimentInfo = experiments.find((exp) => exp.experiment.id === experimentId);
      return (
        components.find((component) => component.variantId === experimentInfo?.selectedVariant?.id)?.component ?? null
      );
    },
    [experiments],
  );

  return (
    <ABTestContext.Provider value={{ experiments, variantConditionalRender, isPartOfVariant }}>
      {children}
    </ABTestContext.Provider>
  );
};

export const useABTest = () => {
  const context = useContext(ABTestContext);
  if (context === undefined) {
    throw new Error('ABTestContext context must be called inside a provider');
  }
  return context;
};
